import React, { useEffect, useState } from 'react';
import { Axios } from '@helpers/ApiService';
import { useNavigate } from 'react-router-dom';
import {
  errorMessage,
  fileUploadConfig,
  onboardingStepId,
  responseTexts,
  storeSetupSubsteps,
} from '@helpers/Constants';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { ButcherHeader } from '@components/ButcherHeader';
import { useToast } from '@contexts/Toast';
import { useFileDropZone } from '@helpers/FileDropZone';
import { CustomerOrderService } from '@services/CustomerService';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { ButcherDetails } from '@helpers/Models';

export const LogoUpload: React.FC = () => {
  const { updateOnboardingStep } = useLocalStorage();
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');
  const [isStepComplete, setIsStepComplete] = useState<boolean>(false);

  const { isDragging, handleDragOver, handleDragLeave, handleDrop } =
    useFileDropZone({ setFile, setError });

  const navigate = useNavigate();
  const { showToastMessage } = useToast();

  const [butcherDetails, setButcherDetails] = useState<ButcherDetails | null>(
    null,
  );
  const [storeLogo, setStoreLogo] = useState<string>();

  useEffect(() => {
    const butcherDetail = CustomerOrderService.getButcherDetails();
    setButcherDetails(butcherDetail);
    console.log('[LogoUpload] butcherDetails loaded:', butcherDetail);

    // Check if the logo step is already complete
    const onboardingSteps = localStorage.getItem('onboardingSteps');
    if (onboardingSteps) {
      const steps = JSON.parse(onboardingSteps);
      const logoStep = steps.find(
        (step: any) => step.stepsId === onboardingStepId.LOGO,
      );
      const complete = logoStep?.isComplete || false;
      setIsStepComplete(complete);
      console.log('[LogoUpload] isStepComplete from localStorage:', complete);
    } else {
      console.log('[LogoUpload] No onboardingSteps found in localStorage');
    }
  }, []);

  /**
   * Handles the file input change event.
   */
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const selectedFile = event.target.files?.[0];

      if (selectedFile) {
        if (selectedFile.size > fileUploadConfig.MAX_FILE_SIZE) {
          setError(errorMessage.MAX_FILE_SIZE_ERROR);
        } else if (!/\.(jpg|png)$/i.test(selectedFile.name)) {
          setError(errorMessage.FILE_FORMATS);
        } else {
          setError('');
          setFile(selectedFile);
          console.log('[LogoUpload] File selected:', selectedFile.name);
        }
      }
    } catch {
      setError(errorMessage.FILE_SELECTION_ERROR);
    }
  };

  /**
   * Handles the upload of the logo (Save action).
   * Does NOT navigate; only saves and updates the step status.
   */
  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!file) {
      setError(errorMessage.UPLOAD_REQUIRED);
      return;
    }

    console.log('[LogoUpload] Save initiated for file:', file.name);

    const formData = new FormData();
    formData.append('logo', file);

    try {
      const response = await Axios.post(ApiEndpoints.CREATE_LOGO, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.status === 'success') {
        setStoreLogo(response.data.data.logoImageUrl);
        const butcherDetails = localStorage.getItem('butcherDetails');

        let details = butcherDetails ? JSON.parse(butcherDetails) : {};
        details = {
          ...details,
          profileImage: {
            ...(details.profileImage || {}),
            logoLocation: response.data.data.logoImageUrl,
          },
        };
        CustomerOrderService.setButcherDetails(details);
        showToastMessage(responseTexts.UPLOAD_SUCCESS, 'success');

        updateOnboardingStep(onboardingStepId.LOGO);
        setIsStepComplete(true);
        setFile(null); // clear the file so user can choose another if needed
        console.log('[LogoUpload] Save successful, isStepComplete set to true, file cleared');
      } else {
        setError(errorMessage.UPLOAD_FAILED);
        console.error('[LogoUpload] Save failed: response status not success', response);
      }
    } catch (err) {
      setError(errorMessage.UPLOAD_FAILED);
      console.error('[LogoUpload] Save error:', err);
    }
  };

  /**
   * Handles the Next action – only navigates.
   * Enabled only when the logo step is complete AND no new file is pending.
   */
  const handleNext = () => {
    console.log('[LogoUpload] Navigating to address page');
    navigate(FrontendRoutes.ADDRESS);
  };

  // Debug: log current state before render
  console.log('[LogoUpload] Render state:', {
    file: file?.name || null,
    isStepComplete,
    hasExistingLogo: !!butcherDetails?.profileImage?.logoLocation,
    nextDisabled: !isStepComplete || file !== null,
  });

  const renderContent = (
    <>
      <div>
        <Breadcrumbs
          title="Configure Settings"
          activeMainNav="Store"
          activeStep={1}
          subNavSteps={storeSetupSubsteps}
          fromSettings={false}
        />
      </div>
      <div className="w-full h-screen flex overflow-hidden">
        <div className="w-full">
          {/* Company Details */}
          <div className="mt-4 text-left text-gray-600 flex items-center">
            <div className="w-24 h-24 bg-[#BDBDBD] border rounded-full mr-3 overflow-hidden">
              {storeLogo || butcherDetails?.profileImage?.logoLocation ? (
                <img
                  className="w-full h-full"
                  src={storeLogo || butcherDetails?.profileImage?.logoLocation}
                  alt="Store Logo"
                />
              ) : null}
            </div>

            <div>
              <p className="text-md text-black">
                {butcherDetails?.companyName}
              </p>

              <p className="text-sm">
                Select and upload the files from your computer
              </p>
            </div>
          </div>
          <form>
            {/* File Input with Drag & Drop */}
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center my-4 transition-colors duration-200 ${isDragging ? 'border-primary bg-primary-50' : 'border-gray-400'
                }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="file"
                className="hidden"
                accept=".jpg,.png"
                onChange={handleFileChange}
              />
              <p className="font-semibold text-gray-700">
                Choose a file or drag & drop it here
              </p>
              <p className="text-gray-500 text-sm mt-1">
                JPEG, PNG formats up to 2 MB
              </p>
              <label
                htmlFor="file"
                className="cursor-pointer mt-3 inline-block py-1 px-4 text-sm font-medium border border-gray-400 text-gray-700 rounded-sm hover:bg-primary hover:text-white"
              >
                Browse File
              </label>
              {error && <p className="text-red-500 mt-2">{error}</p>}
            </div>
          </form>

          {file && !error && (
            <div className="mt-4 bg-gray-200 p-3 rounded-sm relative">
              <button
                onClick={() => {
                  setFile(null);
                  console.log('[LogoUpload] File removed by user');
                }}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 focus:outline-none"
                type="button"
              >
                &times;
              </button>
              <div className="flex items-center gap-3">
                <img
                  src={URL.createObjectURL(file)}
                  alt="Uploaded preview"
                  style={{
                    width: '100px',
                    height: '100px',
                    objectFit: 'cover',
                  }}
                  className="border rounded"
                />
                <div className="flex-1">
                  <p className="text-gray-700 font-medium">{file.name}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between xl:justify-end lg:justify-end mt-8">
            {/* Save button – calls API */}
            <button
              onClick={handleSave}
              disabled={!file}
              className={`w-[50%] xl:max-w-[15%] lg:max-w-[15%] px-6 py-1.5 rounded-md text-xs font-medium focus:outline-none mr-5 transition-colors ${!file
                  ? 'bg-gray-200 text-gray-400 border-gray-300 cursor-not-allowed'
                  : 'text-primary bg-white hover:bg-primary border border-primary hover:text-white'
                }`}
              type="button"
            >
              Save
            </button>

            {/* Next button – navigation, disabled if step not complete or a new file is pending */}
            <button
              onClick={handleNext}
              disabled={!isStepComplete || file !== null}
              className={`w-[50%] xl:max-w-[15%] lg:max-w-[15%] px-6 py-1.5 rounded-md text-xs font-medium focus:outline-none flex items-center justify-center space-x-1 transition-colors ${!isStepComplete || file !== null
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-primary text-white hover:bg-primary'
                }`}
              type="button"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="w-screen h-screen overflow-auto flex justify-center items-start">
      <div className="w-11/12 p-4">
        <ButcherHeader isOnBoarding={true} />
        {renderContent}
      </div>
    </div>
  );
};